import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Bot, Send, Plus, MessageSquare, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, streamChat } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dateShort } from "@/lib/format";
import { CopilotActions } from "@/components/CopilotActions";
import { CopilotMemory } from "@/components/CopilotMemory";

const QUICK = {
  asesor: ["¿Qué leads debo priorizar hoy y por qué?", "Genera el checklist PDF de documentos faltantes de mi lead más avanzado y avísale por WhatsApp", "Descarga mi estado de cuenta de comisiones de este mes en PDF", "Exporta mis leads a CSV", "Crea una liga de onboarding de crédito para compartir en redes", "Genera la propuesta PDF para un cliente con simulación guardada"],
  franquiciatario: ["Resumen del desempeño de mi red esta semana", "Descarga el estado de cuenta de mi franquicia de este mes", "Exporta las solicitudes de mi red a CSV", "Explícame los overrides 5/3/2/1/0.5 con un ejemplo", "Redacta un mensaje motivacional para mi equipo con los números del mes"],
  admin: ["Resumen ejecutivo del pipeline global", "¿Qué franquicias tienen solicitudes con SLA vencido?", "Exporta todas las comisiones a CSV", "¿Qué instituciones responden más rápido?", "Propón acciones para mejorar la conversión de leads a fondeo"],
};

const Bubble = ({ m }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : ""}`}>
    <div className={`msg-bubble max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${m.role === "user" ? "bg-[#FFC629] text-[#0B0B0B]" : "bg-white border border-border"}`}>
      {m.content ? <><ReactMarkdown>{m.content.replace(/@@action[^\n]*/g, "")}</ReactMarkdown><CopilotActions actions={m.actions} /></> : <span className="text-[#8A8A8A]">Pensando…</span>}
    </div>
  </div>
);

export default function Copilot() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [sid, setSid] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef();
  const loadSessions = () => api.get("/copilot/sessions").then(({ data }) => setSessions(data)).catch(() => {});
  useEffect(() => { loadSessions(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  const openSession = (id) => { setSid(id); api.get(`/copilot/history/${id}`).then(({ data }) => setMsgs(data)).catch((e) => toast.error(errMsg(e))); };
  const fresh = () => { setSid(null); setMsgs([]); };

  const patchLast = (fn) => setMsgs((m) => { if (!m.length) return m; const c = [...m]; c[c.length - 1] = fn(c[c.length - 1]); return c; });
  const send = async (text) => {
    const t = (text || input).trim();
    if (!t || busy) return;
    setInput(""); setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: t }, { role: "assistant", content: "" }]);
    try {
      await streamChat("/copilot/chat", { message: t, session_id: sid }, (d) => patchLast((l) => ({ ...l, content: (l.content || "") + d })),
        (j) => { if (j.session_id) { setSid(j.session_id); loadSessions(); } }, (acts) => { patchLast((l) => ({ ...l, actions: acts })); if (acts.some((a) => ["remember", "forget"].includes(a.type))) window.dispatchEvent(new Event("copilot:memory")); });
    } catch (e) { patchLast(() => ({ role: "assistant", content: `⚠️ ${e.message}` })); }
    finally { setBusy(false); setTimeout(() => window.dispatchEvent(new Event("copilot:memory")), 6000); }
  };

  return (
    <div className="grid lg:grid-cols-[260px_1fr] gap-6 h-[calc(100vh-8rem)]" data-testid="copilot-page">
      <aside className="card-warm p-3 flex flex-col min-h-0">
        <button data-testid="copilot-new" onClick={fresh} className="btn-brand w-full !py-2 text-sm"><Plus size={16} /> Nueva conversación</button>
        <div className="mt-3 text-[10px] uppercase tracking-wider text-[#8A8A8A] px-2">Historial</div>
        <div className="mt-1 flex-1 overflow-y-auto space-y-1" data-testid="copilot-sessions">
          {sessions.length === 0 && <p className="px-2 py-3 text-xs text-[#8A8A8A]">Aún no tienes conversaciones.</p>}
          {sessions.map((s) => <button key={s.session_id} data-testid="copilot-session" onClick={() => openSession(s.session_id)} className={`w-full text-left rounded-xl px-3 py-2 text-xs hover:bg-muted ${s.session_id === sid ? "bg-[#FFF6EC] text-[#9A4B00]" : ""}`}><div className="flex items-center gap-1.5 font-medium truncate"><MessageSquare size={12} className="shrink-0" /> {s.title}</div><div className="text-[10px] text-[#8A8A8A] mt-0.5">{dateShort(s.last_at)} · {s.count} mensajes</div></button>)}
        </div>
        <CopilotMemory />
      </aside>
      <section className="card-warm flex flex-col min-h-0">
        <div className="px-5 h-16 flex items-center gap-3 border-b border-border">
          <div className="h-9 w-9 rounded-lg brand-gradient grid place-items-center"><Bot size={18} className="text-[#0B0B0B]" /></div>
          <div><div className="font-display font-bold">Fiin Copilot</div><div className="text-[11px] text-[#8A8A8A]">Claude Sonnet · memoria de largo plazo · genera PDFs y CSV, envía WhatsApp/correo (mock), crea ligas, tareas y mueve estatus</div></div>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-[#FAF8F5]">
          {msgs.length === 0 && (
            <div className="max-w-2xl">
              <p className="eyebrow flex items-center gap-1"><Sparkles size={12} /> Hola, {user.name.split(" ")[0]}</p>
              <h2 className="font-display text-xl font-bold mt-2">¿En qué te ayudo hoy?</h2>
              <div className="mt-4 grid sm:grid-cols-2 gap-2">{(QUICK[user.role] || QUICK.asesor).map((q) => <button key={q} data-testid="copilot-quick" onClick={() => send(q)} className="text-left rounded-xl bg-white border border-border px-4 py-3 text-sm hover:border-[#FF7A00] transition-colors">{q}</button>)}</div>
            </div>
          )}
          {msgs.map((m, i) => <Bubble key={i} m={m} />)}
          <div ref={endRef} />
        </div>
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-border flex gap-2">
          <input data-testid="copilot-page-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Pregunta a Copilot o pídele una acción…" className="flex-1 rounded-full border border-input bg-white px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#FFC629]" />
          <button data-testid="copilot-page-send" disabled={busy || !input.trim()} className="h-10 w-10 rounded-full grid place-items-center bg-[#0B0B0B] text-[#FFC629] disabled:opacity-40"><Send size={16} /></button>
        </form>
      </section>
    </div>
  );
}
